import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { ReferralState } from '#src/__generated__/globalTypes.ts';
import { SBHorizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { range } from '#src/lib/arrays.ts';
import { OPTIMISTIC_SLOTS_COUNT_PER_SPORT } from '#src/lib/so5.ts';
import { getFormattedSport } from '#src/lib/sports.ts';

import type { InProgressReferralBar_Referral } from './__generated__/index.graphql.ts';

const ProgressBar = styled.div`
  display: flex;
  flex: 1;
  gap: 2px;

  & > span {
    height: var(--half-unit);
    background-color: var(--c-nd-200);
    flex: 1;
    border-radius: 1px;
    &.active {
      background-color: var(--c-onBase-success);
    }
  }
`;

type SportProps = { sport: Sport; overrideMax?: number | null; count: number };

const SportRefereeProgression = ({ sport, overrideMax, count }: SportProps) => {
  const max = overrideMax || OPTIMISTIC_SLOTS_COUNT_PER_SPORT[sport];

  return (
    <Vertical gap={0.5}>
      <SBHorizontal>
        <BodyM as="p">
          <FormattedMessage
            defaultMessage="{sport} {count, plural, =0 {card} one {card} other {cards}} collected"
            values={{
              count,
              sport: getFormattedSport(sport),
            }}
          />
        </BodyM>
        <BodyM as="p">
          {count} / {max}
        </BodyM>
      </SBHorizontal>
      <ProgressBar>
        {range(max).map((x, i) => {
          return <span key={x} className={classNames({ active: i < count })} />;
        })}
      </ProgressBar>
    </Vertical>
  );
};

type Props = {
  referral: InProgressReferralBar_Referral;
  sport: Sport;
};

export const InProgressReferralBar = ({ referral, sport }: Props) => {
  return (
    <Vertical gap={2}>
      {ReferralState.PENDING_REVIEW === referral.state && (
        <FormattedMessage defaultMessage="Your referral is under review..." />
      )}
      <SportRefereeProgression
        sport={sport}
        count={referral.refereeSportCardsBoughtFromPrimaryMarketCount}
      />
    </Vertical>
  );
};

InProgressReferralBar.fragments = {
  referral: gql`
    fragment InProgressReferralBar_Referral on Referral {
      id
      state
      refereeSportCardsBoughtFromPrimaryMarketCount(sport: $sport)
    }
  ` as TypedDocumentNode<InProgressReferralBar_Referral>,
};
